<template>
    <v-row>
        <v-col cols="8">
            <h3>{{ $t('performance.title') }}</h3>
            <p>{{ $t('performance.subtitle') }}</p>
            <apexchart type=bubble height=420 :options="optionsRiskProfit" :series="seriesRiskProfit" />
        </v-col>
        <v-col class="d-flex align-center justify-center" cols="2">
            <div>
                <h2>{{ mostProfitableMood }}</h2>
                <p>{{ $t('indicators.mostProfitable') }}</p>

                <h2>{{ lessProfitableMood }}</h2>
                <p>{{ $t('indicators.leastProfitable') }}</p>
            </div>
        </v-col>
        <v-col class="d-flex align-center justify-center" cols="2">
            <div>
                <h2>{{ mostTradedMood }}</h2>
                <p>{{ $t('indicators.mostTraded') }}</p>

                <h2>{{ lessTradedMood }}</h2>
                <p>{{ $t('indicators.fewestTraded') }}</p>
            </div>
        </v-col>
    </v-row>
</template>

<script>
    export default {
        name: "MoodStats",
        data() {
            return {
                mostTradedMood: '',
                lessTradedMood: '',
                mostProfitableMood: '',
                lessProfitableMood: '',

                // Charts
                optionsRiskProfit: {
                    theme: {
                        mode:this.$store.state.currentTheme == "dark"? "dark": "light",
                        palette: 'palette2',
                    },
                    noData: {
                        text: 'No data for the current filter',
                        align: 'center',
                        verticalAlign: 'middle',
                        style: {
                            color: '#111111',
                            fontSize: '21px',
                            fontFamily: 'Ubuntu, sans-serif',
                        }
                    },
                    chart: {
                        height: 350,
                        type: 'bubble',
                    },
                    dataLabels: {
                        enabled: false
                    },
                    fill: {
                        opacity: 0.8
                    },
                    xaxis: {
                        tickAmount: 12,
                        type: 'category',
                        title: {
                            text: 'Monetary Risk'
                        },
                        labels: {
                            formatter: function (value) {
                              return `$${value.toFixed(1)}`;
                            },
                            style: {
                                color: '#6C7079',
                                fontSize: '5px'
                            },
                        }
                    },
                    yaxis: {
                        title: {
                            text: 'Monetary Profit'
                        },
                        labels: {
                            formatter: function (value) {
                              let numberValue = (Math.abs(value) >= 1000000)? `${Math.abs(value / 1000000).toFixed(1)}M` :
                                  (Math.abs(value) >= 1000)? `${Math.abs(value / 1000).toFixed(1)}k` : Math.abs(value.toFixed(0));
                              return (value >= 0)? `$${numberValue}` : `-$${numberValue}`;
                            },
                            style: {
                                color: '#6C7079',
                                fontSize: '5px'
                            },
                        }
                    },
                    tooltip: {
                        z: {
                            formatter: function(val) {
                                return `${val} trades`;
                            },
                            title: 'Count:',
                        },
                        y: {
                            formatter: function(val) {
                                return `$${val.toFixed(1)} of profit`;
                            },
                        },
                        x: {
                            formatter: function(val) {
                                return `$${val.toFixed(1)} of average risk`;
                            },
                        }
                    },
                    annotations: {
                        yaxis: [{
                            y: 0,
                            borderColor: '#111111',
                            label: {
                                show: true,
                                text: 'BreakEven',
                                borderWith: 5,
                                style: {
                                    color: "#FFFFFF",
                                    background: '#111111'
                                }
                            }
                        }]
                    },
                },
                seriesRiskProfit: [],
            };
        },
        mounted() {
            this.setGraphs();
        },
        watch: {
            '$store.state.book.trades': function() {
                this.setGraphs();
            }
        },
        methods: {
            setGraphs() {
                this.seriesRiskProfit = [];
                let copyOfTrades = this.$store.state.book.trades.slice();
                let tradesByMood = this.groupBy(copyOfTrades, 'mood');
                let tradesByMoodArray = Object.entries(tradesByMood);

                // Other stats
                let mostProfitable = Number.MIN_SAFE_INTEGER;
                let lessProfitable = Number.MAX_SAFE_INTEGER;
                let mostTraded = Number.MIN_SAFE_INTEGER;
                let lessTraded = Number.MAX_SAFE_INTEGER;

                tradesByMoodArray.forEach((tradesOfMood) =>{
                    let name = tradesOfMood[0];
                    let netProfitSum = 0;
                    let monetaryRiskSum = 0;
                    let count = tradesOfMood[1].length;
                    tradesOfMood[1].forEach((trade) => {
                        netProfitSum += trade.netProfit;
                        monetaryRiskSum += trade.monetaryRisk;
                    });
                    let data = [[(monetaryRiskSum / count), (netProfitSum / count), count]];
                    this.seriesRiskProfit.push({name, data});

                    // Validate other stats
                    mostTraded = Math.max(mostTraded, count);
                    lessTraded = Math.min(lessTraded, count);
                    mostProfitable = Math.max(mostProfitable, netProfitSum);
                    lessProfitable = Math.min(lessProfitable, netProfitSum);
                    if(lessTraded === count) this.lessTradedMood = name;
                    if(mostTraded === count) this.mostTradedMood = name;
                    if(lessProfitable === netProfitSum) this.lessProfitableMood = name;
                    if(mostProfitable === netProfitSum) this.mostProfitableMood = name;
                });
            },
            groupBy (xs, key) {
                return xs.reduce(function(rv, x) {
                    (rv[x[key]] = rv[x[key]] || []).push(x);
                    return rv;
                }, {});
            },
        },
    }
</script>

<i18n>
{
  "en": {
    "performance": {
      "title": "Performance per Mood",
      "subtitle": "What moods affect my profits and losses the most."
    },
    "indicators": {
      "mostProfitable": "Most Profitable Mood",
      "leastProfitable": "Least Profitable Mood",
      "mostTraded": "Most Traded",
      "fewestTraded": "Fewest Traded"
    }
  },
  "es": {
    "performance": {
      "title": "Rendimiento por Sentimiento",
      "subtitle": "Qué sentimientos afectan más mis ganancias y mis pérdidas."
    },
    "indicators": {
      "mostProfitable": "Sentimiento Más Rentable",
      "leastProfitable": "Sentimiento Menos Rentable",
      "mostTraded": "Más Transado",
      "fewestTraded": "Menos Transado"
    }
  }
}
</i18n>

<style scoped>

</style>
